import { useState, useEffect, useCallback } from 'react';
import useUserInfo from '../useUserInfo';
import useCurrentRestaurant from '../useCurrentRestaurant';

/**
 * Hook to check if the current user has already submitted a review for a specific hiring
 * @param {number} hiringId - The hiring ID to check
 * @param {string} userType - 'profesionales' or 'empresas'
 * @returns {Object} { hasReviewed: boolean, loading: boolean, error: string|null }
 */
const useCheckUserReview = (hiringId, userType) => {
  const [hasReviewed, setHasReviewed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { userInfo } = useUserInfo();
  const { restaurantId: currentRestaurantId } = useCurrentRestaurant();

  const checkReview = useCallback(async () => {
    if (!hiringId || !userInfo) {
      setLoading(false);
      setHasReviewed(false);
      return;
    }

    try {
      setLoading(true);
      
      // Use the new check endpoint that authenticates the user and checks if they've reviewed
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_STAGING_URL}/api/reviews/hiring/${hiringId}/check`,
        {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        // If the new endpoint doesn't exist yet, fallback to the old method
        if (response.status === 404) {
          // Fallback: fetch all reviews and check on frontend
          const fallbackResponse = await fetch(
            `${process.env.REACT_APP_BACKEND_STAGING_URL}/api/reviews/hiring/${hiringId}`,
            {
              method: 'GET',
              credentials: 'include',
              headers: {
                'Content-Type': 'application/json',
              },
            }
          );

          if (!fallbackResponse.ok) {
            throw new Error('Failed to fetch reviews');
          }

          const fallbackData = await fallbackResponse.json();
          
          if (fallbackData.success && Array.isArray(fallbackData.data)) {
            const reviews = fallbackData.data;
            const expectedReviewType = userType === 'profesionales' ? 'restaurant_review' : 'employee_review';
            let userHasReviewed = false;
            
            if (userType === 'profesionales') {
              const employeeId = userInfo.employeeId;
              if (employeeId) {
                userHasReviewed = reviews.some(review => 
                  review.reviewType === expectedReviewType && 
                  review.employeeId === employeeId
                );
              }
            } else {
              const restaurantId = currentRestaurantId || userInfo.restaurants?.[0]?.restaurantId || userInfo.restaurantId;
              if (restaurantId) {
                userHasReviewed = reviews.some(review => 
                  review.reviewType === expectedReviewType && 
                  review.restaurantId === restaurantId
                );
              }
            }
            
            setHasReviewed(userHasReviewed);
          } else {
            setHasReviewed(false);
          }
        } else {
          throw new Error('Failed to check review');
        }
      } else {
        // New endpoint exists and works
        const data = await response.json();
        
        if (data.success && data.data) {
          setHasReviewed(data.data.hasReviewed || false);
        } else {
          setHasReviewed(false);
        }
      }
      
      setError(null);
    } catch (err) {
      console.error('Error checking user review:', err);
      setError(err.message);
      setHasReviewed(false);
    } finally {
      setLoading(false);
    }
  }, [hiringId, userInfo, userType, currentRestaurantId]);

  useEffect(() => {
    checkReview();
  }, [checkReview]);

  return { hasReviewed, loading, error, refreshCheck: checkReview };
};

export default useCheckUserReview;

